'use client'

import { Button, Card, Cell, Divider, InputTextArea, Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { ErrorItem, useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  MediaSelect,
  MediaSelectType,
  useMediaSelectContext,
} from '@marketplace-web/item-upload/media-upload-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  CONVERSATION_URL,
  TransactionDto,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { createComplaint } from '@marketplace-web/complaints/complaints-data'
import { VerificationMethods } from '@marketplace-web/verification/verification-data'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'
import {
  clickEvent,
  noticesUserSubmitTicket,
  noticesUserOpenContactForm,
  userContactSupportEvent,
  createSupportTicket,
  getVerificationPrompt,
  FaqEntryModel,
} from '@marketplace-web/help-center-support-tickets/help-center-support-tickets-data'

import { TERMS_URL, PRIVACY_POLICY_URL, HELP_FAQ_ENTRIES_URL } from './constants/routes'
import EnglishAllowedCheckbox from './EnglishAllowedCheckbox'
import MemberSelect from './MemberSelect'
import TransactionSelect from './TransactionSelect'
import { Field, FormModel } from './types'
import UserEmailField from './UserEmailField'

const MIN_COMPLAINT_MESSAGE_LENGTH = 15
const MAX_NUMBER_OF_IMAGES = 5

type Props = {
  faqEntry: FaqEntryModel
  isLoginTipDisplayed: boolean
  preselectedTransaction: TransactionDto | null
  isEnglishAllowedVisible: boolean
  photoEntryId: number
}

const ContactForm = ({
  faqEntry,
  isLoginTipDisplayed,
  preselectedTransaction,
  isEnglishAllowedVisible,
  photoEntryId,
}: Props) => {
  const { searchParams } = useBrowserNavigation()
  const translate = useTranslate('help_contact_form')
  const { track } = useTracking()

  const formMethods = useForm<FormModel>()
  const {
    handleSubmit,
    getValues,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = formMethods
  const validationMessage = useFormValidationMessage(errors, 'help_contact_form')
  const {
    transactionIdVisible,
    transactionIdRequired,
    memberIdVisible,
    memberIdRequired,
    attachmentsVisible,
    attachmentsRequired,
    submitPhotoTips,
    submitButton,
    isComplaintsFaq,
  } = faqEntry
  const {
    fetch: fetchVerificationPrompt,
    data: verificationPrompt,
    isLoading: isVerificationPromptLoading,
  } = useFetch(getVerificationPrompt)

  const currentUserId = useSession().user?.id
  const { photosCount: uploadedPhotosCount, photoTempIds: uploadedPhotoTempIds } =
    useMediaSelectContext()
  const isValidateCsFormEmailsEnabled = useFeatureSwitch('validate_cs_form_emails')

  const isMaxNumberOfImages20 = useFeatureSwitch('react_hc_contact_form_max_images_count_20')

  const isEmailVerificationCheckEnabled = useFeatureSwitch(
    'react_hc_contact_form_email_verification_check',
  )

  const { user } = useSession()
  const isEmailFieldVisibleForBlockedUser = user?.is_account_banned
  const isEmailCodeVerificationMandatory =
    isEmailVerificationCheckEnabled &&
    verificationPrompt?.prompt.mandatory &&
    verificationPrompt?.prompt.methods.includes(VerificationMethods.EmailCode)

  const isEmailFieldVisible =
    !currentUserId || isEmailFieldVisibleForBlockedUser || isEmailCodeVerificationMandatory

  const [validation, setValidation] = useState<string | null>(null)
  const [photosCount, setPhotosCount] = useState<number>(uploadedPhotosCount)
  const [uiState, setUiState] = useState(UiState.Idle)

  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  useEffect(() => {
    if (!currentUserId || !isEmailVerificationCheckEnabled) return

    fetchVerificationPrompt(currentUserId)
  }, [currentUserId, fetchVerificationPrompt, isEmailVerificationCheckEnabled])

  const { thread_id } = searchParams

  useEffect(() => {
    if (typeof thread_id !== 'string') return

    track(noticesUserOpenContactForm({ threadId: thread_id }))
  }, [thread_id, track])

  useEffect(() => {
    setPhotosCount(uploadedPhotosCount)

    if (photosCount) setValidation(null)
  }, [uploadedPhotosCount, photosCount])

  function validateMessageLength(message: string) {
    return message.trim().length >= MIN_COMPLAINT_MESSAGE_LENGTH
  }

  function handleValidation(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isSubmitting) {
      event.preventDefault()

      return
    }

    if (attachmentsRequired && !photosCount) setValidation(translate('attachments.errors'))
  }

  const setApiErrors = useCallback(
    (apiErrors: Array<ErrorItem>) => {
      if (!isValidateCsFormEmailsEnabled) return

      const emailError = apiErrors.find(({ field }) => field === Field.Email)

      if (emailError) {
        setError(Field.Email, { type: 'manual', message: emailError.value })
      }
    },
    [setError, isValidateCsFormEmailsEnabled],
  )

  async function handleTicketSubmit() {
    if (attachmentsRequired && !photosCount) return

    setUiState(UiState.Pending)

    const { faq_entry_id, problem_text_id } = searchParams
    const formValues = getValues()

    const formattedValues = {
      faqEntryId: Number(faq_entry_id),
      ch: 'help_center',
      problemTextId: problem_text_id,
      transactionId: formValues.transaction,
      threadId: Number(thread_id),
      itemId: formValues.item,
      message: formValues.message,
      memberId: Number(formValues.member),
      isEnglishAllowed: formValues.isEnglishAllowed,
      email: formValues.email || undefined,
      tempUuid: uploadedPhotoTempIds,
      ...(isComplaintsFaq ? {} : { helpCenterSessionId }),
    }

    const response = isComplaintsFaq
      ? await createComplaint(formattedValues)
      : await createSupportTicket(formattedValues)

    if ('errors' in response) {
      if (typeof thread_id === 'string') {
        track(
          userContactSupportEvent({
            helpCenterSessionId,
            oldThreadId: thread_id,
          }),
        )
      }
      setApiErrors(response.errors)

      return
    }

    if (attachmentsRequired && !photosCount) {
      return
    }

    setUiState(UiState.Success)

    const targetDetails = {
      transaction_id: formValues.transaction,
      faq_entry_id,
    }

    if (typeof thread_id === 'string') {
      track(noticesUserSubmitTicket({ threadId: thread_id }))
    }

    track(
      clickEvent({
        target: 'submit',
        screen: 'contact_form',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )

    if (typeof thread_id === 'string' && response.user_msg_thread_id) {
      track(
        userContactSupportEvent({
          helpCenterSessionId,
          oldThreadId: thread_id,
          newThreadId: response.user_msg_thread_id.toString(),
        }),
      )
    } else {
      track(
        userContactSupportEvent({
          helpCenterSessionId,
        }),
      )
    }

    if (currentUserId && response.user_msg_thread_id) {
      navigateToPage(CONVERSATION_URL(response.user_msg_thread_id))
    } else {
      navigateToPage(HELP_FAQ_ENTRIES_URL)
    }
  }

  const renderLegalNote = () => {
    const values = {
      'terms-link': (chunks: Array<ReactNode>) => (
        <a key="terms-link" href={TERMS_URL}>
          {chunks}
        </a>
      ),
      'privacy-link': (chunks: Array<ReactNode>) => (
        <a key="privacy-link" href={PRIVACY_POLICY_URL}>
          {chunks}
        </a>
      ),
    }

    return (
      <Text
        as="h4"
        type={Text.Type.Caption}
        text={
          <div
            style={{
              maxWidth: '80ch',
              textAlign: 'justify',
            }}
          >
            {translate('legal_footer', values)}
          </div>
        }
      />
    )
  }

  if (isVerificationPromptLoading) {
    return <ContentLoader size={ContentLoader.Size.XLarge} testId="verification-prompt-loader" />
  }

  return (
    <form onSubmit={handleSubmit(handleTicketSubmit)}>
      <FormProvider {...formMethods}>
        <Card>
          <Cell
            styling={Cell.Styling.Wide}
            title={
              <Text as="h1" text={translate('header')} theme="amplified" type={Text.Type.Heading} />
            }
          />
          <SeparatedList separator={<Divider />}>
            {transactionIdVisible && (
              <TransactionSelect
                preselectedTransaction={preselectedTransaction}
                isTransactionRequired={transactionIdRequired}
              />
            )}
            {memberIdVisible && <MemberSelect isMemberRequired={memberIdRequired} />}
            {isEmailFieldVisible && (
              <UserEmailField isLoginTipDisplayed={isLoginTipDisplayed && !currentUserId} />
            )}
            {attachmentsVisible && (
              <Cell>
                <MediaSelect
                  type={isComplaintsFaq ? MediaSelectType.Complaint : MediaSelectType.SupportTicket}
                  numberOfImagesPerItem={isMaxNumberOfImages20 ? 20 : MAX_NUMBER_OF_IMAGES}
                  photoEntryId={photoEntryId}
                  customValidationMessage={validation}
                  submitPhotoTips={submitPhotoTips}
                  enableDescription
                />
              </Cell>
            )}
            <InputTextArea
              {...register(Field.Message, {
                required: {
                  value: true,
                  message: translate('message.errors.required', {
                    min: MIN_COMPLAINT_MESSAGE_LENGTH,
                  }),
                },
                validate: message =>
                  validateMessageLength(message) ||
                  translate('message.errors.too_short', { min: MIN_COMPLAINT_MESSAGE_LENGTH }),
              })}
              rows={5}
              placeholder={translate('message.placeholder')}
              styling={InputTextArea.Styling.Wide}
              title={translate('message.title')}
              validation={renderValidation(validationMessage(Field.Message))}
              uncontrolled
            />
            {isEnglishAllowedVisible && <EnglishAllowedCheckbox />}
          </SeparatedList>
          <Spacer />
        </Card>
        <Spacer size={Spacer.Size.Medium} />
        <Cell
          styling={Cell.Styling.Tight}
          suffix={
            <Button
              text={submitButton || translate('submit')}
              disabled={isSubmitting || uiState === UiState.Success}
              isLoading={isSubmitting}
              styling={Button.Styling.Filled}
              size={Button.Size.Medium}
              type={Button.Type.Submit}
              onClick={handleValidation}
            />
          }
        >
          {renderLegalNote()}
        </Cell>
      </FormProvider>
    </form>
  )
}

export default ContactForm
